/**
 * An employee's HR documents — list, register-and-upload, and the access-reason-gated
 * download, WP-W6. `web/shapes/hr-documents.md` §5 is the agreed shape.
 *
 * This mirrors `recruiting/CandidateDocumentsPanel.tsx` exactly, because the HR
 * employee-document surface is the same shape as the candidate-CV one. Three things
 * happen here, and only the third is sensitive:
 *
 *  - **List** the document *metadata* (`GET …/documents`) — never bytes, never a URL, a
 *    bare `{ data: [...] }` with no page, read whole.
 *  - **Register + upload** (`POST …/documents` → a presigned PUT, then the browser PUTs
 *    the bytes straight to object storage). The orchestration is the screen's; this
 *    panel only picks the file and the type.
 *  - **Download** (`GET …/documents/{docId}/download`) — the one sensitive, access-logged
 *    read. It needs an `X-Access-Reason`, so the reason field is the gate: each row's
 *    Download is **disabled until the reason is usable**, which is the one place a
 *    disabled control is honest ("you have not finished saying what you are doing"). The
 *    field says where the reason goes — the shared `access.*` sentence — because a box
 *    collecting one without saying that collects it under false pretences.
 *
 * The reason field validates the same way `reason.ts` does: at least ten characters
 * after trimming, and **Latin-1 only**, because a header value is a ByteString and a
 * Vietnamese reason would throw before the request left the tab. A diacritic reason is
 * refused here, in the field, with the reason said out loud.
 */

import { useState, type ReactNode } from 'react';

import { useI18n, type Translate } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type { HrDocumentType, HrEmployeeDocument } from '../../api/hr';
import { HR_DOCUMENT_TYPES } from '../../api/hr';
import {
  HR_DOCUMENT_TYPE_LABEL,
  type DownloadState,
  type UploadState,
} from '../../hr/employee-document';
import {
  isHeaderSafe,
  isUsableReason,
  MAX_ACCESS_REASON,
  MIN_ACCESS_REASON,
} from '../../access/reason';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

const BYTES_PER_KB = 1024;

export interface EmployeeDocumentsPanelProps {
  readonly status: QueryStatus;
  readonly documents: readonly HrEmployeeDocument[] | null;
  readonly readError: WorkforceApiError | null;
  readonly onReload: () => void;
  readonly locale: string;
  readonly timezone: string;
  readonly registerState: UploadState;
  readonly onRegister: (file: File, docType: HrDocumentType) => void;
  readonly downloadState: DownloadState;
  readonly onDownload: (doc: HrEmployeeDocument, reason: string) => void;
}

export function EmployeeDocumentsPanel({
  status,
  documents,
  readError,
  onReload,
  locale,
  timezone,
  registerState,
  onRegister,
  downloadState,
  onDownload,
}: EmployeeDocumentsPanelProps): ReactNode {
  const { t } = useI18n();
  const [file, setFile] = useState<File | null>(null);
  const [docType, setDocType] = useState<HrDocumentType>('contract');
  const [reason, setReason] = useState('');

  const registering = registerState.status === 'working';
  const reasonUsable = isUsableReason(reason);
  const reasonUnencodable = reason.trim() !== '' && !isHeaderSafe(reason.trim());

  return (
    <section className="wp-section" aria-labelledby="w6d-docs-heading" data-testid="w6d-docs">
      <h3 className="wp-section__title" id="w6d-docs-heading">
        {t('hr.documents.title')}
      </h3>
      <p className="wp-small wp-muted">{t('hr.documents.intro')}</p>

      {/* ── Register + upload ─────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w6d-docs-register">
        <h4 className="wp-section__title">{t('hr.documents.register.title')}</h4>
        <p className="wp-small wp-muted">{t('hr.documents.register.intro')}</p>
        <form
          className="wp-filters"
          onSubmit={(event) => {
            event.preventDefault();
            if (file === null || registering) return;
            onRegister(file, docType);
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w6d-docs-file">
              {t('hr.documents.register.file')}
            </label>
            <input
              className="wp-field__input"
              id="w6d-docs-file"
              data-testid="w6d-docs-file"
              type="file"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
              }}
            />
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w6d-docs-type">
              {t('hr.documents.register.docType')}
            </label>
            <select
              className="wp-field__input"
              id="w6d-docs-type"
              data-testid="w6d-docs-type"
              value={docType}
              onChange={(event) => {
                setDocType(event.target.value as HrDocumentType);
              }}
            >
              {HR_DOCUMENT_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(HR_DOCUMENT_TYPE_LABEL[value])}
                </option>
              ))}
            </select>
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--primary"
              type="submit"
              data-testid="w6d-docs-upload"
              disabled={file === null || registering}
            >
              {registering
                ? t('hr.documents.register.submitting')
                : t('hr.documents.register.submit')}
            </button>
            {file === null ? (
              <p className="wp-small wp-muted" data-testid="w6d-docs-nofile">
                {t('hr.documents.register.noFile')}
              </p>
            ) : null}
          </div>
        </form>
        {registerState.status === 'failed' ? (
          <ApiErrorNote
            error={registerState.error}
            subject={t('hr.documents.subject')}
            testId="w6d-docs-register-error"
          />
        ) : null}
        {registerState.status === 'done' ? (
          <StatusNote
            severity="low"
            title={t('hr.documents.register.ok', {
              filename: registerState.document.filename,
            })}
            testId="w6d-docs-register-ok"
          />
        ) : null}
      </div>

      {/* ── The access reason, gating every download below ────────────────── */}
      {/* Downloading a document writes a row to the audit log, so the reason for it
          is the one thing on this screen drawn as an object rather than a rule: a
          medium wash with a 1px medium border on all four sides (DESIGN.md §4 rule
          5). Everything below it is behind it. */}
      <div className="wp-section__subsection wp-f-gate" data-testid="w6d-docs-access">
        <h4 className="wp-section__title">{t('access.title')}</h4>
        <p>{t('access.why', { subject: t('hr.documents.subjectAccess') })}</p>
        <p className="wp-small wp-muted">{t('access.recorded')}</p>
        <div className="wp-field">
          <label className="wp-field__label" htmlFor="w6d-docs-reason">
            {t('access.label')}
          </label>
          <input
            className="wp-field__input"
            id="w6d-docs-reason"
            data-testid="w6d-docs-reason"
            type="text"
            value={reason}
            maxLength={MAX_ACCESS_REASON}
            autoComplete="off"
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
          <p className="wp-small wp-muted" data-testid="w6d-docs-reason-rule">
            {t('access.rule', { min: MIN_ACCESS_REASON })}
          </p>
          {reasonUnencodable ? (
            <p className="wp-small" role="alert" data-testid="w6d-docs-reason-latin1">
              {t('access.latin1')}
            </p>
          ) : null}
        </div>
      </div>

      {/* ── The list ──────────────────────────────────────────────────────── */}
      {status === 'loading' ? (
        <Skeleton
          rows={3}
          variant="block"
          label={t('hr.documents.subject')}
          testId="w6d-docs-skeleton"
        />
      ) : null}
      {status === 'error' && readError !== null ? (
        <ApiErrorNote
          error={readError}
          onRetry={onReload}
          subject={t('hr.documents.subject')}
          testId="w6d-docs-error"
        />
      ) : null}

      {documents === null ? null : documents.length === 0 ? (
        <EmptyState
          title={t('hr.documents.empty.title')}
          description={t('hr.documents.empty.body')}
          testId="w6d-docs-empty"
        />
      ) : (
        <ul className="wp-rows" data-testid="w6d-docs-list">
          {documents.map((doc) => (
            <li className="wp-row" key={doc.id} data-testid={`w6d-doc-${doc.id}`}>
              <div className="wp-row__main">
                <p>{doc.filename}</p>
                <p className="wp-small wp-muted">
                  {t('hr.documents.meta', {
                    type:
                      doc.doc_type === null
                        ? t('hr.documents.docType.none')
                        : t(HR_DOCUMENT_TYPE_LABEL[doc.doc_type]),
                    size: formatSize(doc.file_size_bytes, t),
                  })}
                  {' · '}
                  {t('hr.documents.uploadedAt', {
                    when: formatInstant(doc.uploaded_at, locale, timezone),
                  })}
                </p>
              </div>
              <div className="wp-row__figure">
                <button
                  className="wp-button"
                  type="button"
                  data-testid={`w6d-doc-download-${doc.id}`}
                  disabled={!reasonUsable || downloadState.status === 'requesting'}
                  onClick={() => {
                    onDownload(doc, reason);
                  }}
                >
                  {downloadState.status === 'requesting' && downloadState.docId === doc.id
                    ? t('hr.documents.downloading')
                    : t('hr.documents.download')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {reasonUsable ? null : (
        <p className="wp-small wp-muted" data-testid="w6d-docs-reason-needed">
          {t('hr.documents.reasonNeeded', { min: MIN_ACCESS_REASON })}
        </p>
      )}

      {downloadState.status === 'refused' ? (
        <ApiErrorNote
          error={downloadState.error}
          subject={t('hr.documents.subjectAccess')}
          testId="w6d-docs-download-error"
        />
      ) : null}
      {downloadState.status === 'delivered' ? (
        <StatusNote
          severity="low"
          title={t('hr.documents.delivered', {
            filename: downloadState.filename,
          })}
          testId="w6d-docs-download-ok"
        />
      ) : null}
    </section>
  );
}

/** A file size in KB, or the "unknown" word when the server recorded none. */
function formatSize(bytes: number | null, t: Translate): string {
  if (bytes === null) return t('hr.documents.sizeUnknown');
  const kb = Math.max(1, Math.round(bytes / BYTES_PER_KB));
  return t('hr.documents.sizeKb', { kb });
}
